// The sun/moon button in every header.
//
// It shows what pressing it will do (a moon while the page is light), which is
// the convention most sites use and the one people look for.

import { useEffect, useState } from "react";
import { applyColorMode, currentColorMode, followSystemColorMode, saveColorMode } from "../lib/colorMode";

export function ColorModeToggle({ className = "" }: { className?: string }) {
  const [mode, setMode] = useState(currentColorMode);

  useEffect(() => {
    // index.html already set the class before first paint; this covers tests
    // and any page that mounts without that script having run.
    applyColorMode(mode);
    return followSystemColorMode(setMode);
  }, []);

  const next = mode === "dark" ? "light" : "dark";

  return (
    <button
      type="button"
      onClick={() => {
        saveColorMode(next);
        setMode(next);
      }}
      aria-label={`Switch to ${next} mode`}
      title={`Switch to ${next} mode`}
      className={
        "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 transition " +
        "hover:bg-slate-100 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 " +
        "focus-visible:outline-offset-2 focus-visible:outline-slate-400 " +
        className
      }
    >
      {next === "dark" ? <MoonIcon /> : <SunIcon />}
    </button>
  );
}

function MoonIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d="M17.29 13.29A8 8 0 0 1 6.71 2.71a8 8 0 1 0 10.58 10.58Z" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d="M10 2a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0V3a1 1 0 0 1 1-1Zm4 8a4 4 0 1 1-8 0 4 4 0 0 1 8 0Zm-.46 4.95.7.7a1 1 0 0 0 1.42-1.41l-.71-.71a1 1 0 0 0-1.41 1.42Zm2.12-10.6a1 1 0 0 1 0 1.41l-.7.71a1 1 0 1 1-1.42-1.42l.71-.7a1 1 0 0 1 1.41 0ZM17 11a1 1 0 1 0 0-2h-1a1 1 0 1 0 0 2h1Zm-7 4a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0v-1a1 1 0 0 1 1-1ZM5.05 6.46a1 1 0 0 0 1.41-1.41l-.7-.71a1 1 0 0 0-1.42 1.41l.71.71Zm1.41 8.49-.7.7a1 1 0 0 1-1.42-1.41l.71-.71a1 1 0 0 1 1.41 1.42ZM4 11a1 1 0 1 0 0-2H3a1 1 0 0 0 0 2h1Z" />
    </svg>
  );
}
